import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck, ArrowRight, X, Fingerprint, Scale, ServerCog,
  UploadCloud, Cog, Download, CircleCheck, FileText,
} from "lucide-react";

const ACCENT = "#2A9D8F";
const INK = "#0f1a21";

/* ---------------------------------------------------------------- header */
const LandingHeader = () => (
  <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
      <Link to="/" className="flex items-center gap-2 font-bold text-slate-900 no-underline" data-testid="landing-brand-home">
        <span className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: ACCENT }}>
          <ShieldCheck size={18} />
        </span>
        AuditPDF
      </Link>
      <Link to="/" data-testid="landing-header-cta"
        className="rounded-full px-5 py-2 text-sm font-semibold text-white no-underline transition-opacity hover:opacity-90"
        style={{ background: INK }}>
        Get Started
      </Link>
    </div>
  </header>
);

/* ------------------------------------------------------------------ hero */
const Hero = ({ eyebrow, headline, sub }) => (
  <section className="mx-auto max-w-6xl px-5 pb-20 pt-16 md:pt-24" data-testid="landing-hero">
    <p className="mb-5 font-mono text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ACCENT }}>
      {eyebrow}
    </p>
    <h1 className="max-w-3xl font-serif text-4xl leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
      {headline}
    </h1>
    <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{sub}</p>
    <div className="mt-9 flex flex-wrap items-center gap-4">
      <Link to="/" data-testid="landing-hero-cta"
        className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white no-underline transition-opacity hover:opacity-90"
        style={{ background: INK }}>
        Get Started <ArrowRight size={16} />
      </Link>
      <a href="#workflow" className="text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-slate-800">
        See how it works
      </a>
    </div>
  </section>
);

/* ------------------------------------------------------------ pain point */
const PainSection = ({ title, points }) => (
  <section className="border-y border-slate-200 bg-slate-50" data-testid="landing-pain">
    <div className="mx-auto max-w-6xl px-5 py-16">
      <h2 className="max-w-xl font-serif text-base text-slate-900 md:text-lg">{title}</h2>
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {points.map((p) => (
          <div key={p.title} className="rounded-lg border border-slate-200 bg-white p-6">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-red-50 text-red-500">
              <X size={16} />
            </span>
            <h3 className="mt-4 text-sm font-bold text-slate-900">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------------------------- exhibit-style preview */
const ExhibitPreview = ({ caseLabel }) => (
  <div role="img" aria-label="Preview of an exhibit-style AuditPDF chat transcript page"
    className="rounded-lg border border-slate-300 bg-white p-5 shadow-[0_20px_50px_-20px_rgba(15,26,33,0.35)]">
    <div className="flex items-center justify-between rounded px-3 py-2 font-mono text-[10px] font-bold text-white" style={{ background: INK }}>
      <span>AuditPDF // SECURE DOCUMENT EXPORT</span>
      <span className="text-slate-400">{caseLabel}</span>
    </div>
    <div className="mt-3 space-y-2.5">
      {[
        { who: "P1 · Michael M.", txt: "Attaching the signed variation for the record.", right: false },
        { who: "P2 · +44 77•• ••••", txt: "Received. I dispute clause 4 as discussed.", right: true },
        { who: "P1 · Michael M.", txt: "Noted — escalating to the case file today.", right: false },
      ].map((m, i) => (
        <div key={i} className={`flex ${m.right ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[75%] rounded border px-3 py-2 ${m.right ? "border-emerald-100 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`}>
            <p className="font-mono text-[9px] font-bold" style={{ color: ACCENT }}>{m.who} · 14:02:11</p>
            <p className="mt-0.5 text-xs text-slate-700">{m.txt}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 border-t border-dashed border-slate-200 pt-3 font-mono text-[9px] text-slate-400">
      SHA-256 9f2c…e41a · Page 4 of 128 · media/IMG-0041.jpg ↗
    </div>
  </div>
);

const SolutionSection = ({ title, body, bullets, caseLabel }) => (
  <section className="mx-auto max-w-6xl px-5 py-20" data-testid="landing-solution">
    <div className="grid items-center gap-12 md:grid-cols-2">
      <div>
        <h2 className="font-serif text-base text-slate-900 md:text-lg">{title}</h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{body}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
              <CircleCheck size={17} className="mt-0.5 flex-shrink-0" style={{ color: ACCENT }} /> {b}
            </li>
          ))}
        </ul>
      </div>
      <ExhibitPreview caseLabel={caseLabel} />
    </div>
  </section>
);

/* ------------------------------------------------- affidavit showcase (legal) */
const AffidavitShowcase = () => (
  <section className="border-y border-slate-200 bg-slate-50" data-testid="landing-affidavit">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 md:grid-cols-2">
      <div role="img" aria-label="Preview of the Affidavit of Authenticity page included in every AuditPDF bundle"
        className="order-2 mx-auto w-full max-w-md rounded-sm border border-slate-300 bg-white p-8 shadow-[0_20px_50px_-20px_rgba(15,26,33,0.35)] md:order-1">
        <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-400">AUDITPDF // FINAL PAGE</p>
        <h3 className="mt-4 font-serif text-2xl text-slate-900">Affidavit of Authenticity</h3>
        <div className="mt-1 h-0.5 w-16" style={{ background: ACCENT }} />
        <p className="mt-5 text-xs leading-relaxed text-slate-600">
          I hereby certify that this document is a true and unaltered rendering of the source
          export identified by the SHA-256 fingerprint below, produced by deterministic,
          offline software without manual editing…
        </p>
        <p className="mt-4 break-all font-mono text-[9px] text-slate-400">
          SHA-256: 9f2c41b8a03e7d6c15f2b9d0aa38c477e2d1905b4cfa88d2317c60f39a1e41a
        </p>
        <div className="mt-8 grid grid-cols-2 gap-6">
          <div><div className="border-t border-slate-400 pt-1 text-[10px] text-slate-500">Signature</div></div>
          <div><div className="border-t border-slate-400 pt-1 text-[10px] text-slate-500">Date</div></div>
        </div>
      </div>
      <div className="order-1 md:order-2">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ACCENT }}>
          Killer feature
        </p>
        <h2 className="mt-3 font-serif text-base text-slate-900 md:text-lg">
          Every bundle closes with an Affidavit of Authenticity
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">
          The final page of every PDF is a pre-drafted certification statement bound to the
          document's cryptographic fingerprint — ready for the deponent to sign. It records the
          generation date, the deterministic processing method, and the SHA-256 hash of the
          source file, giving counsel a defensible chain of custody from export to exhibit.
        </p>
      </div>
    </div>
  </section>
);

/* ----------------------------------------------------------- trust section */
const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Zero-Retention Processing", body: "Source files and bundles are permanently purged within 60 minutes of generation." },
  { icon: Fingerprint, label: "Cryptographic Integrity", body: "SHA-256 fingerprints bind every PDF to its exact source export — independently verifiable." },
  { icon: Scale, label: "GDPR Compliant", body: "Data minimisation by design: no accounts, no stored chat logs, no third-party AI processing." },
  { icon: ServerCog, label: "SOC 2-Aligned Infrastructure", body: "Isolated, encrypted processing environments with audited access controls." },
];

const TrustSection = () => (
  <section className="text-white" style={{ background: INK }} data-testid="landing-trust">
    <div className="mx-auto max-w-6xl px-5 py-16">
      <h2 className="font-serif text-base md:text-lg">Built for Security &amp; Compliance</h2>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_BADGES.map(({ icon: Icon, label, body }) => (
          <div key={label} className="rounded-lg border border-white/10 bg-white/5 p-5">
            <Icon size={20} style={{ color: ACCENT }} />
            <h3 className="mt-3 text-sm font-bold">{label}</h3>
            <p className="mt-2 text-xs leading-relaxed text-slate-400">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------- workflow */
const WORKFLOW_ICONS = [UploadCloud, Cog, Download];

const WorkflowSection = ({ steps }) => (
  <section id="workflow" className="mx-auto max-w-6xl px-5 py-20" data-testid="landing-workflow">
    <h2 className="font-serif text-base text-slate-900 md:text-lg">A three-step, defensible workflow</h2>
    <div className="mt-12 grid gap-8 md:grid-cols-3">
      {steps.map((s, i) => {
        const Icon = WORKFLOW_ICONS[i];
        return (
          <div key={s.title} style={{ transform: `translateY(${i * 22}px)` }}
            className="relative rounded-lg border border-slate-200 bg-white p-6 shadow-sm max-md:!transform-none">
            <span className="font-mono text-xs font-bold text-slate-300">0{i + 1}</span>
            <span className="mt-3 grid h-10 w-10 place-items-center rounded-lg text-white" style={{ background: INK }}>
              <Icon size={18} style={{ color: ACCENT }} />
            </span>
            <h3 className="mt-4 text-sm font-bold text-slate-900">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
          </div>
        );
      })}
    </div>
  </section>
);

/* -------------------------------------------------------------- final CTA */
const FinalCta = ({ headline, sub }) => (
  <section className="border-t border-slate-200 bg-slate-50" data-testid="landing-final-cta">
    <div className="mx-auto max-w-6xl px-5 py-20 text-center">
      <FileText size={26} className="mx-auto" style={{ color: ACCENT }} />
      <h2 className="mx-auto mt-4 max-w-xl font-serif text-base text-slate-900 md:text-lg">{headline}</h2>
      <p className="mx-auto mt-3 max-w-lg text-sm text-slate-600">{sub}</p>
      <Link to="/" data-testid="landing-final-cta-btn"
        className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold text-white no-underline transition-opacity hover:opacity-90"
        style={{ background: INK }}>
        Get Started <ArrowRight size={16} />
      </Link>
      <p className="mt-4 font-mono text-xs text-slate-400">Pay per document · No account · No subscription</p>
    </div>
  </section>
);

const LandingFooter = ({ crossLink }) => (
  <footer className="border-t border-slate-200 bg-white">
    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-8 text-xs text-slate-500">
      <span>© 2026 AuditPDF. All rights reserved.</span>
      <nav className="flex flex-wrap gap-4">
        <Link to={crossLink.to} className="hover:text-slate-800">{crossLink.label}</Link>
        <Link to="/security" className="hover:text-slate-800">Security</Link>
        <Link to="/faq" className="hover:text-slate-800">FAQ</Link>
        <Link to="/verify" className="hover:text-slate-800">Verify a Bundle</Link>
        <Link to="/privacy" className="hover:text-slate-800">Privacy</Link>
      </nav>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ page */
export const SolutionLanding = ({ meta, hero, pain, solution, showAffidavit, workflowSteps, finalCta, crossLink }) => (
  <div className="min-h-screen bg-white text-slate-800" data-testid={meta.testid}>
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={`https://auditpdfhq.com${meta.path}`} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AuditPDF",
          operatingSystem: "Web",
          applicationCategory: "BusinessApplication",
          description: meta.description,
          url: `https://auditpdfhq.com${meta.path}`,
          offers: {
            "@type": "Offer",
            price: "29.00",
            priceCurrency: "EUR",
          },
        })}
      </script>
    </Helmet>
    <LandingHeader />
    <main>
      <Hero {...hero} />
      <PainSection {...pain} />
      <SolutionSection {...solution} />
      {showAffidavit && <AffidavitShowcase />}
      <TrustSection />
      <WorkflowSection steps={workflowSteps} />
      <FinalCta {...finalCta} />
    </main>
    <LandingFooter crossLink={crossLink} />
  </div>
);
